import { useTranslation } from 'react-i18next';
import { Download, FileText } from 'lucide-react';
import { nomeDeArquivo, nomePessoa, textoLivre } from '../../nucleo/formato';
import { useDiscreto } from '../../componentes/sensivel';
import { TextoCortado } from '../../componentes/texto-cortado';
import { pedacosDoTexto, tamanhoArquivo, urlAnexo, type Anexo } from './tipos';

/**
 * Fase 2, W15: texto da mensagem como DADO. Passa pela censura do modo discreto (`textoLivre`) e depois vira pedaços que
 * o React escapa: link http e https clicável (nova aba, `noopener noreferrer`), "@Nome" de quem foi mencionado em
 * destaque e o resto como texto, com as quebras de linha. Nada de HTML.
 */
export function TextoMensagem({ texto, mencionados = [], nomes = [] }: { texto: string; mencionados?: string[]; nomes?: readonly string[] }) {
  // W20: no modo discreto com a opção de nomes, "@Nome" de quem foi mencionado (e os outros nomes do chat) vira as iniciais, e
  // o destaque da menção procura a forma já trocada.
  const pedacos = pedacosDoTexto(
    textoLivre(texto, ...mencionados, ...nomes),
    mencionados.map((n) => nomePessoa(n)),
  );
  return (
    <p className="text-sm break-words whitespace-pre-wrap text-tinta-1">
      {pedacos.map((p, i) =>
        p.tipo === 'link' ? (
          <a key={i} href={p.href} target="_blank" rel="noopener noreferrer" className="text-acento-forte underline underline-offset-2 hover:no-underline">
            {p.valor}
          </a>
        ) : p.tipo === 'mencao' ? (
          <span key={i} data-mencao className="font-semibold text-acento-forte">
            {p.valor}
          </span>
        ) : (
          <span key={i}>{p.valor}</span>
        ),
      )}
    </p>
  );
}

/**
 * Anexos da mensagem: imagem vira miniatura (abre em outra aba), áudio e vídeo tocam, documento baixa. O arquivo vem pela
 * rota do Equipe (só quem lê o canal) e sai sempre como anexo. No modo discreto, a miniatura fica de fora.
 */
export function AnexosMensagem({ canalId, anexos }: { canalId: string; anexos: Anexo[] }) {
  const { t, i18n } = useTranslation('equipe');
  const { ativo } = useDiscreto();
  if (!anexos.length) return null;
  return (
    <ul className="flex min-w-0 flex-col gap-2">
      {anexos.map((a) => {
        const url = urlAnexo(canalId, a.id);
        const nome = nomeDeArquivo(a.nome || a.id.slice(0, 8));
        return (
          <li key={a.id} className="flex min-w-0 flex-col gap-1.5">
            {a.familia === 'imagem' && !ativo && (
              <a href={url} target="_blank" rel="noopener noreferrer" aria-label={t('mensagem.abrirImagem', { nome })} className="block w-fit max-w-full">
                <img src={url} alt={nome} loading="lazy" className="max-h-60 max-w-full rounded-lg border border-borda object-contain" />
              </a>
            )}
            {a.familia === 'audio' && !ativo && <audio controls preload="none" src={url} className="w-full max-w-xs" aria-label={nome} />}
            {a.familia === 'video' && !ativo && <video controls preload="none" src={url} className="max-h-60 max-w-full rounded-lg border border-borda" aria-label={nome} />}
            <a href={url} download className="flex w-fit max-w-full min-w-0 items-center gap-2 rounded-lg border border-borda bg-sup-1 px-2.5 py-1.5 text-xs text-tinta-2 hover:text-tinta-1" aria-label={t('mensagem.baixar', { nome })}>
              {a.familia === 'documento' ? <FileText className="size-4 shrink-0" aria-hidden /> : <Download className="size-4 shrink-0" aria-hidden />}
              <TextoCortado className="min-w-0">{nome}</TextoCortado>
              {a.tamanho > 0 && <span className="numero shrink-0 text-tinta-3">{tamanhoArquivo(a.tamanho, i18n.language)}</span>}
            </a>
          </li>
        );
      })}
    </ul>
  );
}
